import * as React from 'react';
import { Provider } from 'mobx-react';
import { configure } from 'mobx';
import { MessagesStore } from './stores';
import { Messages } from './Messages';
import { TenantSettingsService } from 'intraactive-sdk-helper';
import { ITenantSettings } from '../../interfaces/ITenantSettings';
import { Environment } from '../../services/Environment';
import { IMessagesSettings } from '../../interfaces/IMessagesSettings';
import { AppStore } from '../app/stores';
import { INavigatorContentFromLink } from '../../interfaces/INavigatorContentFromLink';
import DateTimeService from '../../services/DateTimeService';
import { sdkContainerInstance } from '../global';
import { Helper } from '../../Helper';
import { IEnvironment } from '../../interfaces/IEnvironment';
import { IMessage } from '../../interfaces/IMessage';
import { IFontSlots } from '../../interfaces/IFontSlots';
import { IEditableTitleChanges } from '../editableTitle/EditableTitle';
const PACKAGE = require("../../../package.json");

export interface IMessagesProps {
  token: string;
  tenant: string;
  environment: IEnvironment,
  source: "Home" | "Mobile" | "SharePoint";
  settings: IMessagesSettings;
  highlightColor: string;
  rollupHighlightColor?: string;
  webpartId?: string;
  isAdminPage: boolean;
  webpartDisplayMode?: number;
  shouldReQuery?: boolean;
  language?: "Danish" | "English" | "Swedish" | "German";
  isMobileApp?: boolean;
  preview?: boolean;
  hasDarkBackground?: boolean;
  panelIsOpen?: (panelIsOpen: boolean) => void;
  closePanel?: boolean;
  isNavigator?: boolean;
  customTitleElement?: React.ReactNode; // used in messages for command bar and title
  isMessagesTemplateEditor?: boolean; // used in admin to edit and create messages templates
  initialJsonTemplate?: string; // used in admin to edit and create messages templates
  saveTemplateCallback?: (jsonTemplate: string) => void;  // used in admin to edit and create messages templates
  openContentFromLink?: INavigatorContentFromLink; // used to open message from link
  hasOpenedContentFromLinkCallback?: () => void; // used to open message from link
  appStore?: AppStore; // only used in app
  noContentCallback?: () => void; // only used in app
  targetingIsEnabled?: (enabled: boolean) => void // only used in admin
  fontSlots?: IFontSlots;
  themeColors?: string[];
  onTitleSettingsChange?: (changes: IEditableTitleChanges) => void;
}

export class IAMessages extends React.Component<IMessagesProps> {

  private messagesStore: MessagesStore;

  constructor(props: IMessagesProps) {
    super(props);
    this.messagesStore = new MessagesStore();
    configure({
      isolateGlobalState: true,
      enforceActions: "never"
    });
    this.messagesStore.token = props.token;
    this.messagesStore.tenant = props.tenant;
    this.messagesStore.environment = props.environment;
    this.messagesStore.component = "Messages";
    if (this.messagesStore.environment && this.messagesStore.tenant && this.messagesStore.token) {
      const tenantSettings = localStorage.getItem("IA_tenantSettings");
      if (!this.props.isAdminPage && tenantSettings) {
        console.log("Using cached tenant settings");
        this.messagesStore.tenantSettings = JSON.parse(tenantSettings).value;
        if (new DateTimeService().getDatePlusTwoMinuts(new Date(JSON.parse(tenantSettings).date)) > new Date()) {
          this.getTenantSettings();
        }
      } else {
        this.getTenantSettings();
      }
    }
    this.messagesStore.isMessagesTemplateEditor = props.isMessagesTemplateEditor;
    this.messagesStore.showReelsInOneLine = props.settings?.showReelsInOneLine;
    this.messagesStore.showTitleInReelsView = props.settings?.showTitleInReelsView;
    if (props.isMessagesTemplateEditor) {
      let message: IMessage;
      if (props.initialJsonTemplate) {
        message = JSON.parse(props.initialJsonTemplate);
      } else {
        message = {
          title: ""
        }
      }
      this.messagesStore.messageToEdit = message;
    }
    this.updateStore(props);
  }

  public componentDidMount(): void {
    console.log(`%c${this.messagesStore.component === "Event" ? "Events" : "Messages"} Running IntraActive SDK-UI version: ${PACKAGE?.version}`, 'background: #1c899d; color: #ffffff');
  }

  public componentWillReceiveProps(props: IMessagesProps) {
    if (props !== this.props) {
      this.updateStore(props);
      if (
        this.props.settings.instance != props.settings.instance ||
        this.props.settings.numMessages != props.settings.numMessages ||
        this.props.settings.showPinnedMessages != props.settings.showPinnedMessages ||
        this.props.settings.onlyShowPinnedMessages != props.settings.onlyShowPinnedMessages ||
        JSON.stringify(this.props.settings.selectedCategories) != JSON.stringify(props.settings.selectedCategories) ||
        this.props.settings.shouldFilterByCategory != props.settings.shouldFilterByCategory ||
        this.props.settings.isTargetingAvailable != props.settings.isTargetingAvailable
      ) {
        this.messagesStore.reload();
      }
    }
  }

  private updateStore(props: IMessagesProps) {
    if (this.messagesStore.openedFromLink && props.openContentFromLink && props.hasOpenedContentFromLinkCallback) { // abort update when opening a message from link
      return;
    }
    this.messagesStore.token = props.token;
    // only update instance if another instance has been setup
    if (props.settings?.instance?.instanceName !== this.messagesStore.instance?.instanceName) {
      this.messagesStore.instance = {
        ...props.settings.instance,
        id: props.settings.instance?.id,
        instanceName: props.settings.instance.instanceName !== undefined || props.settings.instance.instanceName !== "" ? props.settings.instance.instanceName : "Default"
      }
    }
    this.messagesStore.description = props.settings.description;
    this.messagesStore.header = props.settings.header;
    this.messagesStore.title = props.settings.title;
    this.messagesStore.customTitleElement = props.customTitleElement;
    this.messagesStore.headLineColor = props.settings.headLineColor;
    this.messagesStore.headlineFontSize = props.settings.headlineFontSize;
    this.messagesStore.headlineFontStyle = props.settings.headlineFontStyle;
    this.messagesStore.headlineTextAlign = props.settings.headlineTextAlign;
    this.messagesStore.headlineFontWeight = props.settings.headlineFontWeight;
    this.messagesStore.headlineTextDecoration = props.settings.headlineTextDecoration;
    this.messagesStore.headlineLineHeight = props.settings.headlineLineHeight;
    this.messagesStore.headlineTag = props.settings.headlineTag;
    this.messagesStore.themeColors = props.themeColors;
    this.messagesStore.inheritFont = props.settings.inheritFont;
    this.messagesStore.showReelsInOneLine = props.settings?.showReelsInOneLine;
    this.messagesStore.showTitleInReelsView = props.settings?.showTitleInReelsView;
    this.messagesStore.display = props.settings.display;
    this.messagesStore.numMessages = props.settings.numMessages;
    this.messagesStore.backgroundColor = props.settings.backgroundColor;
    this.messagesStore.overlayColor = props.settings.overlayColor;
    this.messagesStore.textColor = props.settings.textColor;
    this.messagesStore.borderColor = props.settings.borderColor;
    this.messagesStore.borderRadius = props.settings.borderRadius;
    this.messagesStore.selectedCategories = props.settings.selectedCategories;
    if (props.settings) {
      this.messagesStore.filterData.selectedCategories = props.settings.selectedCategories;
    }
    this.messagesStore.shouldFilterByCategory = props.settings.shouldFilterByCategory;
    this.messagesStore.showFilter = props.settings.showFilter;
    this.messagesStore.addMessage = props.settings.addMessage;
    this.messagesStore.showPaging = props.settings.showPaging;
    this.messagesStore.isTargetingAvailable = props.settings.isTargetingAvailable;
    this.messagesStore.isFullPageView = props.settings.isFullPageView;
    this.messagesStore.showCategories = props.settings.showCategories;
    this.messagesStore.showImage = props.settings.showImage;
    this.messagesStore.showAuthor = props.settings.showAuthor;
    this.messagesStore.showTeaser = props.settings.showTeaser;
    this.messagesStore.showTitle = props.settings.showTitle;
    this.messagesStore.showStartDate = props.settings.showStartDate;
    this.messagesStore.showLikes = props.settings.showLikes;
    this.messagesStore.showComments = props.settings.showComments;
    this.messagesStore.showUniqueViews = props.settings.showUniqueViews;
    this.messagesStore.showDropShadow = props.settings.showDropShadow;
    this.messagesStore.showCommandBarItems = props.settings.showCommandBarItems;
    this.messagesStore.showSearch = props.settings.isTargetingAvailable ? props.settings.showSearch : false;
    this.messagesStore.maxAmountOfColumns = props.settings.maxAmountOfColumns;
    this.messagesStore.collapseCommandBarItems = props.settings.collapseCommandBarItems;
    this.messagesStore.showPinnedMessages = props.settings?.showPinnedMessages;
    this.messagesStore.onlyShowPinnedMessages = props.settings?.onlyShowPinnedMessages;
    this.messagesStore.autoRotate = props.settings?.autoRotate;
    this.messagesStore.autoRotateInterval = props.settings?.autoRotateInterval;
    this.messagesStore.carouselInterval = props.settings?.carouselInterval;
    this.messagesStore.showShowAllButton = props.settings?.showShowAllButton;
    this.messagesStore.showAllButtonLink = props.settings?.showAllButtonLink;

    this.messagesStore.enableMarkAllAsRead = props.settings.enableMarkAllAsRead;
    this.messagesStore.onlyShowIfThereIsContent = props.settings.onlyShowIfThereIsContent;
    this.messagesStore.textIfNoContent = props.settings.textIfNoContent;
    this.messagesStore.webpartDisplayMode = props.webpartDisplayMode;
    this.messagesStore.shouldReQuery = props.shouldReQuery;
    this.messagesStore.highlightColor = props.highlightColor;
    this.messagesStore.rollupHighlightColor = props.rollupHighlightColor;
    this.messagesStore.hasDarkBackground = props.hasDarkBackground;
    this.messagesStore.isAdminPage = props.isAdminPage;
    this.messagesStore.language = props.language;
    this.messagesStore.source = props.source;

    this.messagesStore.isMobile = window.innerWidth < 667;
    this.messagesStore.preview = props.preview;
    this.messagesStore.webpartId = props.webpartId;

    this.messagesStore.isMobileApp = props.isMobileApp;

    this.messagesStore.isNavigator = props.isNavigator;
    this.messagesStore.openContentFromLink = props.openContentFromLink;
    this.messagesStore.openedFromLink = props.isNavigator && props.openContentFromLink ? true : false;

    this.messagesStore.fontSlots =  props.fontSlots;
    this.messagesStore.userLocation = props.settings.userLocation;

    if (props.isMobileApp) {
      this.messagesStore.isMobile = true;
      this.messagesStore.appStore = props.appStore ?? undefined;
    }

    if (this.messagesStore.user == undefined) {
      if (localStorage.getItem("IA-user") != undefined) {
        this.messagesStore.user = JSON.parse(localStorage.getItem("IA-user"));
      }
    } else if (this.messagesStore.user != undefined) {
      Helper.setLocalStorage("IA-user", JSON.stringify(this.messagesStore.user));
    }
  }

  private getTenantSettings() {
    console.log("Fetching tenant settings again");
    const tenantSettingsService = new TenantSettingsService<ITenantSettings>(Environment.getEnvironmentForSettingsAPI(this.messagesStore.environment), sdkContainerInstance);
    tenantSettingsService.getSettings(this.messagesStore.tenant, undefined, this.messagesStore.token).then(setting => {
      this.messagesStore.tenantSettings = setting;
      Helper.setLocalStorage("IA_tenantSettings", JSON.stringify({
        date: new Date(),
        value: setting
      }));
    });
  }

  public render(): JSX.Element {
    return (
      <Provider messagesStore={this.messagesStore}>
        <Messages
          instanceId={this.props.settings?.instance?.id}
          panelIsOpen={(panelIsOpen: boolean) => this.props.panelIsOpen(panelIsOpen)}
          noContentCallback={this.props.noContentCallback}
          hasOpenedContentFromLinkCallback={this.props.hasOpenedContentFromLinkCallback}
          targetingIsEnabled={this.props.targetingIsEnabled ? (enabled: boolean) => this.props.targetingIsEnabled(enabled) : undefined}
          saveTemplateCallback={this.props.saveTemplateCallback}
          onTitleSettingsChange={this.props.onTitleSettingsChange}
        />
      </Provider>
    );
  }


}